// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebaseConfig';
import { Capacitor } from '@capacitor/core';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import OrderManagement from './components/order-management/OrderManagement';
import OrderForm from './components/OrderForm';
import Profile from './components/Profile';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import { TrialProvider } from './contexts/TrialContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { isIOSPWA } from './utils/platformUtils';
import OrganizationRegistration from './components/OrganizationRegistration';
import LandingPage from './components/LandingPage';
import MarketingLandingPage from './components/MarketingLandingPage';
import AdminOrganizationApproval from './components/AdminOrganizationApproval';
import Dashboard from './components/Dashboard';
import CrashlyticsService from './services/CrashlyticsService.js';
import NotificationService from './services/NotificationService.tsx';
import ErrorBoundary from './components/ErrorBoundary';
import PlatformLayout from './components/PlatformLayout';
import PlatformButton from './components/PlatformButton';
import PlatformText from './components/PlatformText';
import LoadingScreen from './components/LoadingScreen';
import StatusBar from './components/StatusBar';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import TrialExpirationScreen from './components/TrialExpirationScreen';
import AccountPlan from './components/AccountPlan';
// Inventory components
import InventoryDashboard from './components/inventory/InventoryDashboard';
import ProductsPage from './components/inventory/ProductsPage';
import StockInPage from './components/inventory/StockInPage';
import StockOutPage from './components/inventory/StockOutPage';
import TransactionsPage from './components/inventory/TransactionsPage';
import ReportsPage from './components/inventory/ReportsPage';
import SettingsPage from './components/inventory/SettingsPage';
import InventoryFeatureGate from './components/inventory/InventoryFeatureGate';
import ReceivablesHome from './components/receivables/ReceivablesHome';
import PunchSalePage from './components/receivables/PunchSalePage';
import RecordPaymentPage from './components/receivables/RecordPaymentPage';
import ReceivablesFeatureGate from './components/receivables/ReceivablesFeatureGate';
import './App.css';
import './styles/legal.css';
import { FeatureFlagProvider, useFeatureFlags } from './contexts/FeatureFlagContext';
import './styles/pending-approval.css';

// Loading screen component - now using the unified LoadingScreen
const AppLoadingScreen = () => (
  <LoadingScreen 
    message="Loading..." 
    showSpinner={true} 
    size="large" 
    variant="fullscreen" 
  />
);

// Pending approval screen component
const PendingApprovalScreen = () => {
  const { organizationData, userApproved, loading } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const isOrgApproved = organizationData?.approved === true;
  const isUserApproved = userApproved;

  // Get message from navigation state if available
  const stateMessage = location.state?.message;

  let title = 'Account Pending Approval';
  let mainMessage = '';
  let subMessage = '';
  let icon = '⏳'; // Default icon

  if (!isOrgApproved) {
    title = 'Organization Pending Approval';
    mainMessage = 'Your organization is currently under review by the system administrator.';
    subMessage = '';
    icon = '🏢';
  } else if (!isUserApproved) {
    title = 'Pending Approval';
    mainMessage = stateMessage || 'Your account is pending approval by the business owner.';
    subMessage = '';
    icon = '👤';
  }

  const handleSignOut = async () => {
    await auth.signOut();
    navigate('/');
  };

  // Show loading state if organization data is still loading
  if (loading) {
    return (
      <PlatformLayout>
        <div className="pending-approval-container">
          <div className="pending-approval-card">
            <div className="pending-approval-loading">
              <PlatformText variant="body">Loading...</PlatformText>
            </div>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout>
      <div className="pending-approval-container">
        <div className="pending-approval-card">
          {/* Icon */}
          <div className="pending-approval-icon">
            {icon}
          </div>
          
          {/* Title */}
          <PlatformText variant="h2">{title} ⏳</PlatformText>
          
          {/* Message */}
          <div className="pending-approval-message">
            <PlatformText variant="body">{mainMessage}</PlatformText>
            {subMessage && (
              <PlatformText variant="body" className="pending-approval-submessage">{subMessage}</PlatformText>
            )}
          </div>
          
          {/* Sign out button */}
          <PlatformButton 
            onClick={handleSignOut} 
            className="pending-approval-signout"
            variant="outline"
          >
            Sign Out
          </PlatformButton>
        </div>
      </div>
    </PlatformLayout>
  );
};

// AuthWrapper handles all auth and organization state
const AuthWrapper = ({ children }) => {
  const location = useLocation();
  const [user, authLoading, authError] = useAuthState(auth);
  const { 
    loading: orgLoading, 
    error, 
    organizationId, 
    isApproved,
    canAccessApp,
    isTrialExpired,
    organizationData,
    trialStatus
  } = useOrganization();

  const isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  const allowOrgRegistration = !isNativeIOS;
  const basePublicPaths = ['/login', '/register', '/', '/landing', '/pending-approval', '/trial-expired', '/privacy-policy', '/terms-of-service'];
  const publicPaths = allowOrgRegistration ? [...basePublicPaths, '/register-organization'] : basePublicPaths;
  const isRegisterOrgRoute = location.pathname === '/register-organization';
  const isAllowedRegisterOrgRoute = allowOrgRegistration && isRegisterOrgRoute;
  const isPublicPath = publicPaths.includes(location.pathname);
  
  // Status bar visibility management for iOS PWA
  // Hide status bar on marketing landing page (/) or when user is not logged in
  // Only applies to iOS PWA - other platforms are unaffected
  useEffect(() => {
    // Only manage status bar for iOS PWA
    if (!isIOSPWA()) {
      return;
    }
    
    // Hide status bar on marketing landing page OR when user not logged in
    const shouldHideStatusBar = location.pathname === '/' || !user;
    
    document.body.classList.toggle('status-bar-hidden', shouldHideStatusBar);
    
    // Cleanup: remove class on unmount (though AuthWrapper rarely unmounts)
    return () => {
      document.body.classList.remove('status-bar-hidden');
    };
  }, [location.pathname, user]);
  
  // Handle authentication error
  if (authError) {
    return <Navigate to="/login" replace state={{ error: authError.message }} />;
  }

  // If no user, only allow access to public routes
  if (!user) {
    return isPublicPath ? children : <Navigate to="/login" replace />;
  }

  // Handle loading state
  if (authLoading || orgLoading) {
    return <AppLoadingScreen />;
  }

  // If organization error, log out and redirect
  if (error) {
    if (location.pathname !== '/register' && !isAllowedRegisterOrgRoute) {
      auth.signOut();
      return <Navigate to="/login" replace state={{ error }} />;
    }
    return children;
  }

  // If user is authenticated but not approved, redirect to pending approval page
  if (!isApproved) {
    if (location.pathname !== '/pending-approval' && !isAllowedRegisterOrgRoute) {
      return <Navigate to="/pending-approval" replace />;
    }
    return children;
  }

  // User is authenticated and approved, but doesn't have an organization
  // This check must happen before trial expiration check since trial status requires organization data
  if (!organizationId) {
    if (location.pathname !== '/register' && !isAllowedRegisterOrgRoute) {
      return <Navigate to="/register" replace />;
    }
    return children;
  }

  // If user is approved but trial/subscription is expired, redirect to trial expiration page
  // This check happens after organizationId check since trial status depends on organization
  // Allow access to /account-plan and (when permitted) /register-organization when expired (for BusinessOwner to upgrade)
  if (isApproved && isTrialExpired) {
    if (
      location.pathname !== '/trial-expired' &&
      location.pathname !== '/account-plan' &&
      !isAllowedRegisterOrgRoute
    ) {
      return <Navigate to="/trial-expired" replace />;
    }
    return children;
  }

  // Final access check - user must be able to access the app
  // Check if access is denied due to expiration (before scheduled function updates status)
  // This handles the case where subscriptionStatus is still 'active' but subscriptionEndsAt has passed
  if (!canAccessApp) {
    // Check if this is an expiration scenario (not a different access issue)
    const subscriptionStatus = trialStatus?.subscriptionStatus || organizationData?.subscriptionStatus;
    const subscriptionEndsAt = organizationData?.subscriptionEndsAt || null;
    
    // If subscription is expired or active subscription has passed its end date, redirect to expiration screen
    // This prevents login loop by showing expiration screen instead of signing out
    if (subscriptionStatus === 'expired' || 
        (subscriptionStatus === 'active' && subscriptionEndsAt)) {
      // Normalize subscriptionEndsAt to Date for comparison
      let expiryDate = null;
      if (subscriptionEndsAt) {
        if (typeof subscriptionEndsAt === 'string') {
          expiryDate = new Date(subscriptionEndsAt);
        } else if (subscriptionEndsAt && typeof subscriptionEndsAt.toDate === 'function') {
          // Firestore Timestamp
          expiryDate = subscriptionEndsAt.toDate();
        } else if (subscriptionEndsAt instanceof Date) {
          expiryDate = subscriptionEndsAt;
        }
      }
      
      // If subscription has expired, redirect to expiration screen instead of signing out
      if (subscriptionStatus === 'expired' || (expiryDate && expiryDate <= new Date())) {
        if (
          location.pathname !== '/trial-expired' &&
          location.pathname !== '/account-plan' &&
          !isAllowedRegisterOrgRoute
        ) {
          return <Navigate to="/trial-expired" replace />;
        }
        return children;
      }
    }
    
    // If access is denied for a different reason (not expiration), sign out and redirect to login
    // This should not happen if the above checks are working correctly
    // But as a safety net, redirect to login
    auth.signOut();
    return <Navigate to="/login" replace state={{ error: 'Access denied' }} />;
  }

  // User is authenticated, has an organization, is approved, and has valid trial/subscription
  // Redirect from public routes to the main application (except legal documents)
  if (isPublicPath && location.pathname !== '/privacy-policy' && location.pathname !== '/terms-of-service') {
    return <Navigate to="/orders" replace />;
  }

  // All checks passed, render children
  return children;
};

// Simple role check component
const RoleCheck = ({ children, allowedRoles }) => {
  const { userRole } = useOrganization();
  
  // Only allow access if allowedRoles includes userRole
  if (!allowedRoles || allowedRoles.includes(userRole)) {
    return children;
  }
  
  return <Navigate to="/orders" replace />;
};

// AppRoutes component that contains all the routes
const AppRoutes = () => {
  const isNative = Capacitor.isNativePlatform();
  const isNativeIOS = isNative && Capacitor.getPlatform() === 'ios';
  const allowOrgRegistration = !isNativeIOS;
  const navigate = useNavigate();
  const { inventory: inventoryEnabled, receivables: receivablesEnabled } = useFeatureFlags();
  
  // Set global navigate function for deep linking from notifications
  useEffect(() => {
    const { setGlobalNavigate } = require('./utils/navigationUtils');
    setGlobalNavigate(navigate);
  }, [navigate]);
  
  return (
    <AuthWrapper>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={
          isNative ? <Navigate to="/login" replace /> : <MarketingLandingPage />
        } />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={<LoginForm />} />
        <Route path="/register" element={<RegisterForm />} />
        {allowOrgRegistration && (
          <Route path="/register-organization" element={<OrganizationRegistration />} />
        )}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        
        {/* Protected routes */}
        <Route path="/admin/organization-approval" element={<AdminOrganizationApproval />} />
        <Route path="/pending-approval" element={<PendingApprovalScreen />} />
        <Route path="/trial-expired" element={<TrialExpirationScreen />} />
        <Route path="/orders" element={<OrderManagement />} />
        <Route 
          path="/new-order" 
          element={
            <RoleCheck allowedRoles={['Salesperson', 'BusinessOwner', 'Admin', 'Accountant']}>
              <OrderForm />
            </RoleCheck>
          } 
        />
        <Route path="/profile" element={<Profile />} />
        <Route 
          path="/account-plan" 
          element={
            <RoleCheck allowedRoles={['BusinessOwner']}>
              <AccountPlan />
            </RoleCheck>
          } 
        />
        <Route path="/dashboard" element={<Dashboard />} />

        {receivablesEnabled ? (
          <>
            <Route
              path="/receivables"
              element={
                <ReceivablesFeatureGate>
                  <RoleCheck allowedRoles={['BusinessOwner', 'Accountant', 'Salesperson']}>
                    <ReceivablesHome />
                  </RoleCheck>
                </ReceivablesFeatureGate>
              }
            />
            <Route
              path="/receivables/new"
              element={
                <ReceivablesFeatureGate>
                  <RoleCheck allowedRoles={['BusinessOwner', 'Accountant']}>
                    <PunchSalePage />
                  </RoleCheck>
                </ReceivablesFeatureGate>
              }
            />
            <Route
              path="/receivables/edit/:id"
              element={
                <ReceivablesFeatureGate>
                  <RoleCheck allowedRoles={['BusinessOwner', 'Accountant']}>
                    <PunchSalePage />
                  </RoleCheck>
                </ReceivablesFeatureGate>
              }
            />
            <Route
              path="/receivables/pay/:id"
              element={
                <ReceivablesFeatureGate>
                  <RoleCheck allowedRoles={['BusinessOwner', 'Accountant']}>
                    <RecordPaymentPage />
                  </RoleCheck>
                </ReceivablesFeatureGate>
              }
            />
          </>
        ) : (
          <>
            <Route path="/receivables" element={<Navigate to="/orders" replace />} />
            <Route path="/receivables/*" element={<Navigate to="/orders" replace />} />
          </>
        )}
        
        {/* Inventory Routes */}
        {inventoryEnabled ? (
          <>
        <Route 
          path="/inventory" 
          element={
                <InventoryFeatureGate>
            <RoleCheck allowedRoles={['InventoryManager', 'BusinessOwner', 'Accountant', 'Salesperson']}>
              <InventoryDashboard />
            </RoleCheck>
                </InventoryFeatureGate>
          } 
        />
        <Route 
          path="/inventory/products" 
          element={
                <InventoryFeatureGate>
            <RoleCheck allowedRoles={['InventoryManager', 'BusinessOwner', 'Accountant', 'Salesperson']}>
              <ProductsPage />
            </RoleCheck>
                </InventoryFeatureGate>
          } 
        />
        <Route 
          path="/inventory/stock-in" 
          element={
                <InventoryFeatureGate>
            <RoleCheck allowedRoles={['InventoryManager', 'BusinessOwner', 'Accountant']}>
              <StockInPage />
            </RoleCheck>
                </InventoryFeatureGate>
          } 
        />
        <Route 
          path="/inventory/stock-out" 
          element={
                <InventoryFeatureGate>
            <RoleCheck allowedRoles={['InventoryManager', 'BusinessOwner', 'Accountant']}>
              <StockOutPage />
            </RoleCheck>
                </InventoryFeatureGate>
          } 
        />
        <Route 
          path="/inventory/transactions" 
          element={
                <InventoryFeatureGate>
            <RoleCheck allowedRoles={['InventoryManager', 'BusinessOwner', 'Accountant']}>
              <TransactionsPage />
            </RoleCheck>
                </InventoryFeatureGate>
          } 
        />
        <Route 
          path="/inventory/reports" 
          element={
                <InventoryFeatureGate>
            <RoleCheck allowedRoles={['InventoryManager', 'BusinessOwner', 'Accountant']}>
              <ReportsPage />
            </RoleCheck>
                </InventoryFeatureGate>
          } 
        />
        <Route 
          path="/inventory/settings" 
          element={
                <InventoryFeatureGate>
            <RoleCheck allowedRoles={['BusinessOwner', 'Accountant']}>
              <SettingsPage />
            </RoleCheck>
                </InventoryFeatureGate>
          } 
        />
          </>
        ) : (
          <>
            <Route path="/inventory" element={<Navigate to="/orders" replace />} />
            <Route path="/inventory/*" element={<Navigate to="/orders" replace />} />
          </>
        )}
        
        <Route path="*" element={<Navigate to="/orders" replace />} />
      </Routes>
    </AuthWrapper>
  );
};

// Main App component
function App() {
  useEffect(() => {
    // Initialize services
    const initializeServices = async () => {
      try {
        // Notification service will be initialized after user login
        // This prevents permission requests before authentication

        // Initialize Crashlytics service
        const crashlyticsService = CrashlyticsService.getInstance();
        await crashlyticsService.initialize();

        // Set app version and build number from environment variables
        const appVersion = process.env.REACT_APP_VERSION || '1.0.1';
        const buildNumber = process.env.REACT_APP_BUILD_NUMBER || '2';
        await crashlyticsService.setAppVersion(appVersion);
        await crashlyticsService.setBuildNumber(buildNumber);
      } catch (error) {
        // Failed to initialize services - handled silently
      }
    };

    initializeServices();

    return () => {
      // Cleanup services when app unmounts
      try {
        const notificationService = NotificationService.getInstance();
        notificationService.cleanup();
      } catch (error) {
        // App cleanup error - handled silently
      }
    };
  }, []);

  return (
    <ErrorBoundary componentName="App">
      <StatusBar />
      <Router>
        <ErrorBoundary componentName="AuthProvider">
          <AuthProvider>
            <ErrorBoundary componentName="ErrorProvider">
              <ErrorProvider>
                <ErrorBoundary componentName="OrganizationProvider">
                  <OrganizationProvider>
                    <ErrorBoundary componentName="TrialProvider">
                      <TrialProvider>
                        <ErrorBoundary componentName="NotificationProvider">
                          <NotificationProvider>
                            <ErrorBoundary componentName="FeatureFlagProvider">
                              <FeatureFlagProvider>
                            <ErrorBoundary componentName="AppRoutes">
                              <AppRoutes />
                                </ErrorBoundary>
                              </FeatureFlagProvider>
                            </ErrorBoundary>
                          </NotificationProvider>
                        </ErrorBoundary>
                      </TrialProvider>
                    </ErrorBoundary>
                  </OrganizationProvider>
                </ErrorBoundary>
              </ErrorProvider>
            </ErrorBoundary>
          </AuthProvider>
        </ErrorBoundary>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
